// @ts-nocheck
// Extended wrapper based on ui-kit/untitled/base/select/{select,select-item}.tsx
// Adds: `description` field to SelectItemType, rendered as a second line
// in both the dropdown items and the trigger value display.
// When upgrading Untitled UI to a new version (via `npx untitledui add select
// --overwrite`), review this file for parity with the updated vendored source.
"use client"

import { ChevronDown } from "@untitledui/icons"
import { Check } from "@untitledui/icons"
import type { ComponentProps, FC, ReactNode, Ref, RefAttributes } from "react"
import { isValidElement, useContext } from "react"
import {
  Button as AriaButton,
  ListBox as AriaListBox,
  ListBoxItem as AriaListBoxItem,
  Select as AriaSelect,
  SelectValue as AriaSelectValue,
  Text as AriaText,
} from "react-aria-components"
import type { SelectProps as AriaSelectProps } from "react-aria-components"
import type { ListBoxItemProps as AriaListBoxItemProps } from "react-aria-components"
import type { FieldError } from "react-hook-form"

import type { ComboBoxProps } from "@/components/ui-kit/untitled/base/select/combobox"
import { Popover } from "@/components/ui-kit/untitled/base/select/popover"
import {
  Select as VendoredSelect,
  SelectContext,
  sizes,
} from "@/components/ui-kit/untitled/base/select/select"
import { NativeSelect as KitNativeSelect } from "@/components/ui-kit/untitled/base/select/select-native"
// Read ComboBox off the vendored Select instead of importing ./combobox directly.
// Direct import flips the load order against the vendored select.tsx ⇄ combobox.tsx
// cycle and trips a TDZ on `_Select.ComboBox = ComboBox`.

import { Avatar } from "@uui/base/avatar/avatar"
import { HintText } from "@uui/base/input/hint-text"
import { Label } from "@uui/base/input/label"
import { cx } from "@uui/utils/cx"
import { isReactComponent } from "@uui/utils/is-react-component"

const ComboBox: FC<ComboBoxProps> = VendoredSelect.ComboBox

/* Control heights from the digest (28/32/36/44): an `sm` select is 32, `md` 36. */
const HEIGHT = { sm: "h-8 py-0 text-sm", md: "h-9 py-0 text-sm" }

/* A popover floats over the page, so it is paper that lifts: stone.50, a hairline, shadow.lift. */
const PAPER_POPOVER = "bg-primary ring-secondary shadow-lift"

// Extended type that adds description to the vendored SelectItemType
export interface SelectItemType {
  id: string
  label?: string
  avatarUrl?: string
  isDisabled?: boolean
  supportingText?: string
  description?: string
  icon?: FC | ReactNode
}

export interface CommonProps {
  hint?: string
  label?: string
  tooltip?: string
  size?: "sm" | "md"
  placeholder?: string
}

export interface SelectProps
  extends
    Omit<AriaSelectProps<SelectItemType>, "children" | "items">,
    RefAttributes<HTMLDivElement>,
    CommonProps {
  items?: SelectItemType[]
  popoverClassName?: string
  placeholderIcon?: FC | ReactNode
  children: ReactNode | ((item: SelectItemType) => ReactNode)
  error?: FieldError
}

interface SelectValueProps {
  isOpen: boolean
  size: "sm" | "md"
  isFocused: boolean
  isDisabled: boolean
  placeholder?: string
  ref?: Ref<HTMLButtonElement>
  placeholderIcon?: FC | ReactNode
  error?: FieldError
}

/* 30px rows at sm, 36 at md; 12.5px labels at 500, 14px icons in ink.quiet, the current value ticked in ink. */
const itemSizes = {
  sm: "h-[30px] px-2 py-0",
  md: "h-9 px-2.5 py-0",
}

const SelectValue = ({
  isOpen,
  isFocused,
  isDisabled,
  size,
  placeholder,
  placeholderIcon,
  ref,
  error,
}: SelectValueProps) => {
  return (
    <AriaButton
      ref={ref}
      className={cx(
        "bg-primary ring-primary relative flex w-full cursor-pointer items-center rounded-lg shadow-xs ring-1 outline-hidden transition duration-100 ease-linear ring-inset",
        (isFocused || isOpen) && "ring-brand ring-2",
        error && "ring-error_subtle",
        isDisabled && "bg-disabled_subtle text-disabled cursor-not-allowed",
      )}
    >
      <AriaSelectValue<SelectItemType>
        className={cx(
          "flex h-max w-full items-center justify-start gap-2 truncate text-left align-middle",
          "*:data-icon:text-fg-quaternary in-disabled:*:data-icon:text-fg-disabled *:data-icon:size-5 *:data-icon:shrink-0",
          sizes[size].root,
          HEIGHT[size],
        )}
      >
        {(state) => {
          const Icon = state.selectedItem?.icon || placeholderIcon
          return (
            <>
              {state.selectedItem?.avatarUrl ? (
                <Avatar
                  size="xs"
                  src={state.selectedItem.avatarUrl}
                  alt={state.selectedItem.label}
                />
              ) : isReactComponent(Icon) ? (
                <Icon data-icon aria-hidden="true" />
              ) : isValidElement(Icon) ? (
                Icon
              ) : null}

              {state.selectedItem ? (
                <section className="flex w-full flex-col truncate">
                  <div className="flex gap-2">
                    <p className="text-md text-primary truncate font-medium">
                      {state.selectedItem?.label}
                    </p>
                    {state.selectedItem?.supportingText && (
                      <p className="text-md text-tertiary">
                        {state.selectedItem?.supportingText}
                      </p>
                    )}
                  </div>
                  {state.selectedItem?.description && (
                    <p className="text-quaternary truncate text-sm">
                      {state.selectedItem.description}
                    </p>
                  )}
                </section>
              ) : (
                <p
                  className={cx(
                    "text-md text-placeholder",
                    isDisabled && "text-disabled",
                  )}
                >
                  {placeholder}
                </p>
              )}

              <ChevronDown
                aria-hidden="true"
                className={cx(
                  "text-fg-quaternary ml-auto shrink-0",
                  size === "sm" ? "size-4 stroke-[2.5px]" : "size-5",
                )}
              />
            </>
          )
        }}
      </AriaSelectValue>
    </AriaButton>
  )
}

interface SelectItemProps
  extends Omit<AriaListBoxItemProps<SelectItemType>, "id">, SelectItemType {}

const SelectItem = ({
  label,
  id,
  value,
  avatarUrl,
  supportingText,
  description,
  isDisabled,
  icon: Icon,
  className,
  children,
  ...props
}: SelectItemProps) => {
  const { size } = useContext(SelectContext)

  const labelOrChildren =
    label || (typeof children === "string" ? children : "")
  const textValue = supportingText
    ? labelOrChildren + " " + supportingText
    : labelOrChildren

  return (
    <AriaListBoxItem
      id={id}
      value={
        value ?? {
          id,
          label: labelOrChildren,
          avatarUrl,
          supportingText,
          description,
          isDisabled,
          icon: Icon,
        }
      }
      textValue={textValue}
      isDisabled={isDisabled}
      {...props}
      className={(state) =>
        cx(
          "w-full px-1.5 py-px outline-hidden",
          typeof className === "function" ? className(state) : className,
        )
      }
    >
      {(state) => (
        <div
          className={cx(
            "flex cursor-pointer items-center gap-2 rounded-md outline-hidden select-none",
            state.isDisabled && "cursor-not-allowed",
            state.isFocused && "bg-secondary_subtle",
            state.isFocusVisible && "ring-focus-ring ring-2 ring-inset",
            "*:data-icon:text-quaternary *:data-icon:size-3.5 *:data-icon:shrink-0",
            state.isDisabled && "*:data-icon:text-fg-disabled",
            itemSizes[size],
          )}
        >
          {avatarUrl ? (
            <Avatar aria-hidden="true" size="xs" src={avatarUrl} alt={label} />
          ) : isReactComponent(Icon) ? (
            <Icon data-icon aria-hidden="true" />
          ) : isValidElement(Icon) ? (
            Icon
          ) : null}

          <div className="flex w-full min-w-0 flex-1 flex-col">
            <div className="flex flex-wrap gap-x-2">
              <AriaText
                slot="label"
                className={cx(
                  "text-secondary truncate text-[12.5px] font-medium whitespace-nowrap",
                  state.isSelected && "text-primary font-semibold",
                  state.isDisabled && "text-disabled",
                )}
              >
                {label ||
                  (typeof children === "function" ? children(state) : children)}
              </AriaText>

              {supportingText && (
                <AriaText
                  slot="description"
                  className={cx(
                    "text-tertiary text-[12.5px] whitespace-nowrap",
                    state.isDisabled && "text-disabled",
                  )}
                >
                  {supportingText}
                </AriaText>
              )}
            </div>
            {description && (
              <p
                className={cx(
                  "text-quaternary truncate text-sm",
                  state.isDisabled && "text-disabled",
                )}
              >
                {description}
              </p>
            )}
          </div>

          {state.isSelected && (
            <Check
              aria-hidden="true"
              className={cx(
                "text-primary ml-auto",
                size === "sm" ? "size-3.5 stroke-[2.5px]" : "size-4",
                state.isDisabled && "text-fg-disabled",
              )}
            />
          )}
        </div>
      )}
    </AriaListBoxItem>
  )
}

const Select = ({
  placeholder = "Select",
  placeholderIcon,
  size = "sm",
  children,
  items,
  label,
  hint,
  tooltip,
  className,
  error,
  ...rest
}: SelectProps) => {
  return (
    <SelectContext.Provider value={{ size }}>
      <AriaSelect
        {...rest}
        className={(state) =>
          cx(
            "flex flex-col gap-1.5",
            typeof className === "function" ? className(state) : className,
          )
        }
      >
        {(state) => (
          <>
            {label && (
              <Label isRequired={state.isRequired} tooltip={tooltip}>
                {label}
              </Label>
            )}

            <SelectValue
              {...state}
              {...{ size, placeholder }}
              placeholderIcon={placeholderIcon}
              error={error}
            />

            <Popover
              size={size}
              className={cx(PAPER_POPOVER, rest.popoverClassName)}
            >
              <AriaListBox items={items} className="size-full outline-hidden">
                {children}
              </AriaListBox>
            </Popover>

            {error && <HintText isInvalid>{error.message}</HintText>}
            {hint && !error && <HintText>{hint}</HintText>}
          </>
        )}
      </AriaSelect>
    </SelectContext.Provider>
  )
}

const _Select = Select as typeof Select & {
  ComboBox: typeof ComboBox
  Item: typeof SelectItem
}
_Select.ComboBox = ComboBox
_Select.Item = SelectItem

export { _Select as Select }
export type NativeSelectProps = ComponentProps<typeof KitNativeSelect>

/* The native select takes the `sm` control height; its classes sit on the <select> inside. */
export const NativeSelect = ({ className, ...props }: NativeSelectProps) => (
  <KitNativeSelect
    className={cx(
      "[&_select]:h-8 [&_select]:py-0 [&_select]:text-sm [&_select]:font-normal",
      className,
    )}
    {...props}
  />
)
